/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, August 21st 2022, 5:56:18 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import FormControl from "@mui/material/FormControl";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { OSPopUpAPIForm, TextFormInput, DropDownFormInput, FormSubmitBubblingStopper } from "ui-widgets/forms";
import { ToggleState, Typography } from "ui-widgets";
import { NextActivation, CONDITION_LOGIC_TYPES } from "lib/schemas";
import {
  useNextActivationAPIForm,
  UseNextActivationAPIFormSpec,
} from "service-builder/services/OutgoingBranchesConfigurationAPI";
import { TaskTemplateInput } from "./TaskTemplateInput";
import { useServiceBuilderContext } from "service-builder/providers/ServiceBuilderContext";
import { ActivationConditionList } from "./ActivationConditionList";

type ActivationBranchFormProps = UseNextActivationAPIFormSpec & Pick<ToggleState, "handleClose" | "open">;

export const ActivationBranchForm = (props: ActivationBranchFormProps) => {
  const { currentProcessDefinition, showConditionsList, watchedActivation, useAPIFormReturn, handleClose } =
    useActivationBranchForm(props);

  return (
    <FormSubmitBubblingStopper>
      <OSPopUpAPIForm
        popUpIcon={<AccountTreeIcon color="primary" />}
        popUpTitle="Outgoing Branch Configuration"
        useAPIFormReturn={useAPIFormReturn}
        loaderType="form-skeleton"
        open={props.open}
        onClose={handleClose}
      >
        <FormControl>
          <Typography color="primary" variant="h6">
            Outgoing Branch Details
          </Typography>
        </FormControl>
        <TextFormInput<NextActivation> label="Outgoing Branch Name" schema_field_key="description" />

        <TaskTemplateInput<NextActivation>
          schema_field_key="ontrue"
          label="Choose the next task that should be triggered by this branch."
          apiQueryParams={{ process_definition: currentProcessDefinition.id }}
        />
        <FormControl>
          <Typography color="primary" variant="h6">
            Outgoing Branch Conditions
          </Typography>
        </FormControl>
        <DropDownFormInput<NextActivation>
          schema_field_key="condition_logic_type"
          options={CONDITION_LOGIC_TYPES}
          label="This branch will be activated if"
          helperText="Specify which conditions should be met to activate the next task specified in this branch. If no conditions are set, the next task configured for this branch will always be activated at this step of the workflow."
        />
        {showConditionsList && <ActivationConditionList nextActivation={watchedActivation} />}
      </OSPopUpAPIForm>
    </FormSubmitBubblingStopper>
  );
};

const useActivationBranchForm = ({
  nextActivationFetchId,
  nextActivationDefaultValues,
  updateNextActivationFromAPICallback,
  onSuccessfulAPISave,
  open,
  handleClose,
}: ActivationBranchFormProps) => {
  const useAPIFormReturn = useNextActivationAPIForm({
    nextActivationFetchId,
    nextActivationDefaultValues,
    updateNextActivationFromAPICallback,
    onSuccessfulAPISave,
  });

  const {
    useFormReturn: {
      watch,
      formState: { isDirty },
    },
    submitAPIForm,
  } = useAPIFormReturn;

  const watchedActivation: NextActivation = watch();

  const showConditionsList = watchedActivation.condition_logic_type && watchedActivation.id;

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  React.useEffect(() => {
    // Auto Submit Before Adding Conditions
    if (!watchedActivation?.id && isDirty) submitAPIForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedActivation?.id, watchedActivation?.condition_logic_type]);

  return { currentProcessDefinition, open, showConditionsList, watchedActivation, useAPIFormReturn, handleClose };
};
